import React from 'react';
import {
    DatePicker,
    Card,
    ExtendedSelect,
    InputText,
    ICardProps,
    PhoneInput,
    SexRadios,
    VerifyEmailInput,
} from 'components';
import { Sex } from 'components/enums';
import { Button, Form, Col } from 'react-bootstrap';
import { RegExpConst, RegExpErrorsText } from '_helpers';
import { IApiError } from '../../../_helpers/ApiConnector/types';
import moment from 'moment';
import styles from './PersonalData.module.scss';
import cn from 'classnames';
import _ from 'lodash';

const PersonalData = ({
    stateData,
    cancel,
    apiError,
    onSubmit,
    onlyWatch,
    sendConfirmationEmail,
    regions,
    cardProps,
    withoutPaddingFrom,
    submitButtonText,
    positionPlaceHolder,
}: Props) => {
    const initErrors: IInitErrors = {
        regNumber: '',
        firstName: '',
        lastName: '',
        middleName: '',
        phone: '',
        sex: '',
        email: '',
        birthDate: '',
        regionId: '',
        position: '',
    };

    const initFormData: IFormData = {
        regNumber: '',
        firstName: '',
        lastName: '',
        middleName: '',
        phone: '',
        sex: Sex.Male,
        email: '',
        regionId: -1,
        emailConfirmed: false,
        birthDate: undefined,
        region: '',
        position: '',
    };

    const [formData, setFormData] = React.useState<IFormData>(initFormData);
    const [validErrors, setValidErrors] = React.useState(initErrors);
    const regionOptions = React.useMemo(() => (regions || [{ id: formData.regionId, name: formData.region }]).map(v => ({ value: `${v.id}`, label: v.name })), [formData]);

    React.useEffect(() => {
        if (stateData) {
            setFormData({ ...stateData, position: stateData.position || '' });
        }
    }, [stateData]);

    React.useEffect(() => {
        if (apiError && apiError.failures) {
            const errors = apiError.failures;
            const newErrors: IInitErrors = { ...initErrors };

            for (const key in errors) {
                if (newErrors[key] !== undefined) {
                    newErrors[key] = errors[key].join(',');
                }
            }
            setValidErrors({ ...newErrors });
        } else {
            setValidErrors(initErrors);
        }
    }, [apiError]);

    const handleDateChange = (val: string, momentDate: moment.Moment) => {
        setFormData({ ...formData, birthDate: momentDate });
        setValidErrors({ ...validErrors, birthDate: '' });
    };

    const checkFieldsErrors = (data: IFormData) => _.mapValues(data, (val, key) => {
        const test = (field: string, regexp: RegExp) => (!regexp.test(field));

        if (key === 'position' || key === 'region') return '';
        if (val === '') return 'Обязательное поле для заполнения';
        if (key === 'regionId' && val === -1) return 'Обязательное поле для заполнения';
        if (key === 'birthDate' && !val) return 'Обязательное поле для заполнения';
        if ((key === 'firstName' || key === 'lastName' || key === 'middleName') && typeof val === 'string') {
            const error = test(val, RegExpConst.NAME);

            if (error) {
                return RegExpErrorsText.NAME;
            }

            return '';
        }
        if (key === 'phone' && typeof val === 'string') {
            const error = test(val, RegExpConst.PHONE);

            if (error) {
                return RegExpErrorsText.PHONE;
            }

            return '';
        }
        if (key === 'email' && typeof val === 'string') {
            const error = test(val, RegExpConst.EMAIL);

            if (error) {
                return RegExpErrorsText.EMAIL;
            }

            return '';
        }

        return '';
    });

    const isValidFormData = (fieldsError: IInitErrors): boolean => _.values(fieldsError).every(e => e.length === 0);

    const submit = async (e?: React.FormEvent<HTMLElement>) => {
        if (e) {
            e.preventDefault();
        }

        let res: boolean | IPerson | null = null;

        const temp = checkFieldsErrors(formData);
        const errors: IInitErrors = { ...temp, position: temp.position || '', birthDate: temp.birthDate || '' };

        setValidErrors(errors);

        if (onSubmit && formData.birthDate && isValidFormData(errors)) {
            res = await onSubmit({ ...formData, birthDate: formData.birthDate });
        }

        return !!res;
    };

    const saveAndCloseDetectFunc = async () => {
        const res = await submit();
        return res;
    };

    const onInputChange = (name: string, value: string) => {
        setFormData({ ...formData, [name]: value });
        setValidErrors({ ...validErrors, [name]: '' });
    };

    return (
        <Card
          cardClassName="border-0"
          title="Личные данные эксперта"
          category="Информация по личным данным эксперта"
          saveAndCloseDetectFunc={stateData ? saveAndCloseDetectFunc : undefined}
          detectFormChanges={!!stateData}
          withoutBottomMargin={!onlyWatch}
          withoutPadding
          {...cardProps}
        >
            <Form onSubmit={submit} className={cn(!withoutPaddingFrom && styles.form)}>
                <InputText
                  name="regNumber"
                  value={formData.regNumber}
                  labelTitle="РЕГ.НОМЕР"
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  onChange={onInputChange}
                  withDarkLabel
                />
                <InputText
                  name="lastName"
                  value={formData.lastName}
                  labelTitle="ФАМИЛИЯ"
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  onChange={onInputChange}
                  withDarkLabel
                />
                <InputText
                  name="firstName"
                  value={formData.firstName}
                  labelTitle="ИМЯ"
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  onChange={onInputChange}
                  withDarkLabel
                />
                <InputText
                  name="middleName"
                  value={formData.middleName}
                  labelTitle="ОТЧЕСТВО"
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  onChange={onInputChange}
                  withDarkLabel
                />

                <Form.Group className={`row ${styles.align_center}`}>
                    <Col sm="2" className="p-0">
                        <Form.Label column>
                            ДАТА РОЖД.
                        </Form.Label>
                    </Col>
                    <Col sm="5">
                        <DatePicker disabled={onlyWatch} onChange={handleDateChange} initialDate={formData.birthDate} isInvalid={validErrors.birthDate} />
                    </Col>
                </Form.Group>
                <Form.Group className={`row ${styles.align_center}`}>
                    <Col sm="2" className="p-0">
                        <Form.Label column>
                            РЕГИОН
                        </Form.Label>
                    </Col>
                    <Col sm="5">
                        <ExtendedSelect
                          name="regionId"
                          value={`${formData.regionId}`}
                          options={regionOptions}
                          disabled={onlyWatch}
                          isInvalid={Boolean(validErrors.regionId)}
                          onChange={(e) => {
                                setFormData({ ...formData, regionId: parseInt(e.value) });
                                setValidErrors({ ...validErrors, regionId: '' });
                            }}
                        />
                        <Form.Control.Feedback className="d-block" type="invalid">{validErrors.regionId}</Form.Control.Feedback>
                    </Col>
                </Form.Group>
                <SexRadios
                  sex={formData.sex}
                  disabled={onlyWatch}
                  validErrors={validErrors}
                  onClick={(newSex) => {
                        setFormData({ ...formData, sex: newSex as Sex });
                        setValidErrors({ ...validErrors, sex: '' });
                    }}
                />
                <PhoneInput
                  value={formData.phone}
                  labelTitle="ТЕЛЕФОН"
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  phoneHelperClassName="pl-3"
                  onChange={onInputChange}
                  withDarkLabel
                />

                <VerifyEmailInput
                  value={formData.email}
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  sendConfirmationEmail={formData.emailConfirmed ? undefined : sendConfirmationEmail}
                  onChange={onInputChange}
                  withDarkLabel
                />

                <InputText
                  name="position"
                  value={formData.position || ''}
                  labelTitle="ДОЛЖНОСТЬ"
                  labelColSize={2}
                  inputColSize={5}
                  disabled={onlyWatch}
                  fieldFailures={validErrors}
                  placeholder={positionPlaceHolder}
                  onChange={onInputChange}
                  inputGroupClassNames="mb-0"
                  withDarkLabel
                />

                {!onlyWatch && (
                    <>
                        <Form.Group className="row mb-0">
                            <Col sm={{ span: 3, offset: 2 }}>
                                <Button className="action-button" block type="submit">{submitButtonText || (stateData ? 'Сохранить' : 'Следующий шаг')}</Button>
                            </Col>
                            <Col sm={3}>
                                <Button className="action-button" block variant="outline-secondary" onClick={cancel}>Отмена</Button>
                            </Col>
                        </Form.Group>
                    </>
                )}
            </Form>
        </Card>
    );
};

export default PersonalData;

export interface Props {
    apiError: IApiError | null;
    regions?: IRegion[] | null;
    onlyWatch?: boolean;
    stateData?: IStateData;
    cardProps?: ICardProps;
    withoutPaddingFrom?: boolean;
    submitButtonText?: string;
    positionPlaceHolder?: string;
    cancel: () => void;
    sendConfirmationEmail?: () => void;
    onSubmit?: (data: IPerson) => Promise<IPerson | null | boolean>;
}

export interface IPerson {
    regNumber: string;
    firstName: string;
    lastName: string;
    middleName: string;
    phone: string;
    sex: Sex;
    email: string;
    birthDate: moment.Moment;
    regionId: number;
    position?: string;
}

interface IRegion {
    id: number;
    name: string;
}

interface IStateProps {
    emailConfirmed: boolean;
    region: string;
}

type IStateData = IPerson & IStateProps;

interface IFormData extends Omit<IStateData, 'birthDate'> {
    birthDate?: moment.Moment;
}

interface IInitErrors {
    regNumber: string;
    firstName: string;
    lastName: string;
    middleName: string;
    phone: string;
    sex: string;
    email: string;
    birthDate: string;
    regionId: string;
    position: string;
    [key: string]: string;
}
